import { useQuery } from '@tanstack/react-query';

import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import apiService from 'src/shared/config/apiService';
import handleAxiosError from 'src/shared/utils/handleAxiosError';

import { getBookings } from '../api/getBookings';
import { getBookingTypes } from '../api/getBookingTypes';

// backend/notifications/models.py > DISCOVERY_BOOKING_TYPES
const DISCOVERY_BOOKING_TYPE_NAMES = [
  'Cavela Discovery (Platform)',
  'Cavela Discovery (Meta Ads)',
  'Cavela Discovery (Astris Outbound)',
  'Cavela Discovery (Growth DNA)'
];

export const localDiscoveryBookingKey = (orgId: number) =>
  `cavela_discovery_booked_${orgId}`;

export const markDiscoveryBookedLocally = (orgId: number) => {
  try {
    localStorage.setItem(localDiscoveryBookingKey(orgId), '1');
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
};

export const hasLocalDiscoveryBooking = (orgId: number): boolean => {
  try {
    return localStorage.getItem(localDiscoveryBookingKey(orgId)) === '1';
  } catch {
    return false;
  }
};

// backend/cavela/api.py > organization (GET)
interface OrgClassificationResponse {
  name: string;
  classification: string | null;
}

const getOrgClassification = async (orgId: number): Promise<string | null> => {
  try {
    const res = await apiService.get<OrgClassificationResponse>(
      `/organizations/${orgId}/`
    );

    return res.data.classification;
  } catch (error) {
    throw handleAxiosError(error);
  }
};

export interface OrgOnboardingGate {
  classification: string | null;
  has_discovery_booking: boolean;
  discovery_calendly_url: string;
}

export const useOrgOnboardingGate = (enabled: boolean) => {
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );
  const orgId = selectedOrganization?.id;

  return useQuery({
    queryKey: ['org-onboarding-gate', orgId],
    queryFn: async (): Promise<OrgOnboardingGate> => {
      if (!orgId) throw new Error('No org selected');

      // Skip all network calls if already booked locally
      if (hasLocalDiscoveryBooking(orgId)) {
        return {
          classification: null,
          has_discovery_booking: true,
          discovery_calendly_url: ''
        };
      }

      const [bookings, bookingTypes, classification] = await Promise.all([
        getBookings({
          organizationId: orgId,
          bookingTypeNames: DISCOVERY_BOOKING_TYPE_NAMES,
          includeAllStatuses: true
        }),
        getBookingTypes(),
        getOrgClassification(orgId)
      ]);

      const hasDiscoveryBooking = bookings.length > 0;
      const discoveryType = bookingTypes.find((t) =>
        DISCOVERY_BOOKING_TYPE_NAMES.includes(t.name)
      );

      return {
        classification,
        has_discovery_booking: hasDiscoveryBooking,
        discovery_calendly_url: discoveryType?.effective_calendly_url ?? ''
      };
    },
    enabled: enabled && !!orgId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true
  });
};
